import * as monthUtils from '@actual-app/core/shared/months';

/**
 * Meses cubiertos por adelantado (widget «Meses cubiertos» del Inicio).
 *
 * Un mes futuro está cubierto cuando ya tiene asignado tanto como el mes
 * actual (la «necesidad» de un mes). Se cuentan los meses seguidos desde el
 * siguiente: los llenos suman 1 y el primero que no está lleno suma su
 * fracción y corta la cuenta. Así, noviembre lleno y diciembre al 60 % dan
 * 1,6 meses, aunque enero tenga algo asignado.
 *
 * Sin React: cifras en céntimos y positivas (la hoja guarda lo asignado en
 * negativo; `zero_budgets.amount`, en positivo).
 */

export type MesCubierto = {
  /** `YYYY-MM`. */
  mes: string;
  asignado: number;
  /** 0..1: asignado / necesidad, recortado. */
  fraccion: number;
};

export type MesesCubiertos = {
  /** Lo asignado en el mes actual: lo que vale un mes lleno. */
  necesidad: number;
  /** Meses cubiertos seguidos desde el siguiente (1,6 = 1 lleno y 60 %). */
  meses: number;
  /** Último día cubierto (`YYYY-MM-DD`); null si no hay nada cubierto. */
  hasta: string | null;
  /**
   * Meses siguientes para dibujar: al menos `ventanaMinima`, y si hay dinero
   * más lejos, hasta el último mes con algo asignado y uno vacío más (como
   * mucho un año).
   */
  porMes: MesCubierto[];
  /** Primer mes sin llenar y lo que le falta; null si todos están llenos. */
  siguiente: { mes: string; falta: number } | null;
};

/** Hasta cuántos meses se cuenta (un año por delante). */
export const HORIZONTE = 12;

export function calcularMesesCubiertos({
  mesActual,
  asignadoPorMes,
  ventanaMinima = 4,
}: {
  mesActual: string;
  /** Asignado por mes (`YYYY-MM` → céntimos, positivo). */
  asignadoPorMes: ReadonlyMap<string, number>;
  ventanaMinima?: number;
}): MesesCubiertos {
  const necesidad = Math.max(0, asignadoPorMes.get(mesActual) ?? 0);
  const fraccion = (mes: string) => {
    if (necesidad <= 0) {
      return 0;
    }
    const valor = (asignadoPorMes.get(mes) ?? 0) / necesidad;
    return Number.isFinite(valor) ? Math.max(0, Math.min(1, valor)) : 0;
  };

  let meses = 0;
  let siguiente: MesesCubiertos['siguiente'] = null;
  let hasta: string | null = null;
  for (let i = 1; i <= HORIZONTE && necesidad > 0; i++) {
    const mes = monthUtils.addMonths(mesActual, i);
    const f = fraccion(mes);
    if (f >= 1) {
      meses += 1;
      hasta = monthUtils.lastDayOfMonth(mes);
      continue;
    }
    meses += f;
    siguiente = {
      mes,
      falta: necesidad - Math.max(0, asignadoPorMes.get(mes) ?? 0),
    };
    if (f > 0) {
      const dias = Number(monthUtils.lastDayOfMonth(mes).slice(8, 10));
      const cubiertos = Math.floor(f * dias);
      if (cubiertos > 0) {
        hasta = monthUtils.addDays(
          monthUtils.firstDayOfMonth(mes),
          cubiertos - 1,
        );
      }
    }
    break;
  }

  let ultimoConDinero = 0;
  for (let i = 1; i <= HORIZONTE; i++) {
    if ((asignadoPorMes.get(monthUtils.addMonths(mesActual, i)) ?? 0) > 0) {
      ultimoConDinero = i;
    }
  }
  const ventana = Math.min(
    HORIZONTE,
    Math.max(ventanaMinima, ultimoConDinero + 1),
  );

  const porMes = Array.from({ length: ventana }, (_, i) => {
    const mes = monthUtils.addMonths(mesActual, i + 1);
    return {
      mes,
      asignado: Math.max(0, asignadoPorMes.get(mes) ?? 0),
      fraccion: fraccion(mes),
    };
  });

  return {
    necesidad,
    meses: Math.round(meses * 10) / 10,
    hasta,
    porMes,
    siguiente,
  };
}
